import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import {
  IAgentSchema,
  IBusinessShoppingCenterSchema,
  IOfferSchema,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { IconReliable } from './icons/icon_reliable';
import * as styles from './index.css';
import { trackClick, trackKPLinkClick } from './tracking';
import { Link } from '../../common/components/link';
import { getFormattedDeadline } from '../../utils/getFormattedDeadline';
import { IParent, ParentType } from '../../utils/offer_helpers';
import { getOfferType, isCommercial, FOfferType } from '../../utils/offer_type';
import { getCustomUrl } from '../../utils/tracking/getCustomUrl';

export interface IParentProps {
  agent: IAgentSchema | null;
  offer: IOfferSchema;
  parent: IParent | undefined;
  containerClassName?: string;
  businessShoppingCenter: IBusinessShoppingCenterSchema | null;
  withoutPretext?: boolean;
}

const BuildingTypePrepositional: {
  [index: string]: string;
} = {
  administrativeBuilding: 'административном здании',
  businessCenter: 'бизнес-центре',
  businessCenter2: 'деловом центре',
  businessHouse: 'деловом доме',
  businessPark: 'бизнес-парке',
  businessQuarter: 'бизнес-квартале',
  businessQuarter2: 'деловом квартале',
  free: 'свободном здании',
  freeAppointmentObject: 'объекте свободного назначения',
  industrialComplex: 'производственном комплексе',
  industrialPark: 'индустриальном парке',
  industrialSite: 'промплощадке',
  industrialWarehouseComplex: 'производственно-складском комплексе',
  logisticsCenter: 'логистическом центре',
  logisticsComplex: 'логистическом комплексе',
  mansion: 'особняке',
  manufactureBuilding: 'производственном здании',
  manufacturingFacility: 'производственном цехе',
  modular: 'модульном здании',
  multifunctionalComplex: 'многофункциональном комплексе',
  officeAndHotelComplex: 'офисно-гостиничном комплексе',
  officeAndResidentialComplex: 'офисно-жилом комплексе',
  officeAndWarehouse: 'офисно-складском здании',
  officeAndWarehouseComplex: 'офисно-складском комплексе',
  officeBuilding: 'офисном здании',
  officeCenter: 'офисном центре',
  officeComplex: 'офисном комплексе',
  officeIndustrialComplex: 'офисно-производственом комплексе',
  officeQuarter: 'офисном квартале',
  outlet: 'аутлете',
  propertyComplex: 'имущественном комплексе',
  residentialComplex: 'жилом комплексе',
  residentialHouse: 'жилом доме',
  shoppingAndBusinessComplex: 'торгово-деловом комплексе',
  shoppingAndCommunityCenter: 'торгово-общественном центре',
  shoppingAndEntertainmentCenter: 'торгово-развлекательном центре',
  shoppingCenter: 'торговом центре',
  shoppingComplex: 'торговом комплексе',
  specializedShoppingCenter: 'специализированном торговом центре',
  standaloneBuilding: 'здании',
  technopark: 'технопарке',
  tradeAndExhibitionComplex: 'торгово-выставочном комплексе',
  tradingHouse: 'торговом доме',
  tradingOfficeComplex: 'торгово-офисном комплексе',
  warehouse: 'складе',
  warehouseComplex: 'складском комплексе',
  shoppingAndWarehouseComplex: 'торгово-складском комплексе',
};

export class Parent extends React.PureComponent<IParentProps, {}> {
  public render() {
    const {
      containerClassName,
      parent,
      offer: { newbuilding, deadline },
      withoutPretext,
    } = this.props;

    let isGKReliable = false;
    let houseStatus = '';
    let houseName = '';

    const offerType = getOfferType(this.props.offer);
    const isCommercialCard =
      isCommercial(offerType) &&
      ![FOfferType.FreeAppointmentObject, FOfferType.Office, FOfferType.TradeArea, FOfferType.Warehouse].includes(
        offerType,
      );

    /* istanbul ignore next */
    if (newbuilding && !isCommercialCard) {
      const { house } = newbuilding;
      isGKReliable = newbuilding.showJkReliableFlag || false;
      houseStatus = getFormattedDeadline(deadline, house);

      if (house) {
        houseName = house.name;
      }
    }

    if (!parent) {
      return null;
    }

    return (
      <div
        {...mergeStyles(
          containerClassName ? containerClassName : styles['container'],
          withoutPretext && styles['large'],
        )}
      >
        {parent.url ? (
          <Link className={styles['link']} href={parent.url} target="_blank" rel="noopener" onClick={this.handleClick}>
            {this.formatParentName()}
          </Link>
        ) : (
          <strong>{this.formatParentName()}</strong>
        )}
        {houseName && houseName !== parent.name && <span>{`, ${houseName}`}</span>}
        {houseStatus && <span className={styles['status']}>{`, ${houseStatus}`}</span>}
        {isGKReliable && (
          <div className={styles['reliable']}>
            <IconReliable /> Проверено ЦИАН
          </div>
        )}
      </div>
    );
  }

  private handleClick = () => {
    const { parent } = this.props;

    if (parent && parent.type === ParentType.JK) {
      trackClick(
        getCustomUrl(this.props.offer, this.props.agent, {
          buildingType: 'JK',
        }),
      );
    } else if (parent && parent.type === ParentType.KP) {
      trackKPLinkClick(
        getCustomUrl(this.props.offer, this.props.agent, {
          buildingType: 'KP',
        }),
      );
    }
  };

  private formatParentName(): string | undefined {
    const { businessShoppingCenter, offer, parent, withoutPretext } = this.props;
    let prefix = '';

    /* istanbul ignore next */
    if (!parent) {
      return undefined;
    }

    if (parent.type === ParentType.JK) {
      prefix = 'ЖК';
    } else if (parent.type === ParentType.KP) {
      prefix = 'коттеджном посёлке';
    } else {
      if (
        businessShoppingCenter &&
        businessShoppingCenter.buildingType &&
        BuildingTypePrepositional[businessShoppingCenter.buildingType]
      ) {
        prefix = BuildingTypePrepositional[businessShoppingCenter.buildingType];
      } else if (offer.building && offer.building.type) {
        prefix = BuildingTypePrepositional[offer.building.type];
      }
    }

    if (parent.type === ParentType.Coworking) {
      return withoutPretext ? `Коворкинг «${parent.name}»` : `в коворкинге «${parent.name}»`;
    }

    return `в ${prefix} «${parent.name}»`;
  }
}
